import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CatalogBanner } from '@/components/layout/CatalogBanner';

export const RAZON_SOCIAL =
  'Soluciones Empresariales en Tecnología y Protección del Norte, S. de R.L. de C.V.';
export const NOMBRE_COMERCIAL = 'Grupo Alarcom';
export const CONTACTO_LEGAL = 'ventas@alarcom.mx';
export const SITIO_CORPORATIVO = 'https://www.alarcom.mx';
export const FECHA_LEGAL = '15 de septiembre de 2026';
export const OPENPAY_TERMINOS = 'https://www.openpay.mx/terminos-condiciones';
export const OPENPAY_PRIVACIDAD = 'https://www.openpay.mx/aviso-privacidad';

function LegalLayout({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <>
      <CatalogBanner
        title={title}
        subtitle={`${NOMBRE_COMERCIAL} · última actualización ${FECHA_LEGAL}`}
        breadcrumb={[{ label: title }]}
      />
      <div className="page-section">
        <article className="container-app prose-legal max-w-3xl space-y-6 text-sm leading-relaxed text-surface-700">
          {children}
        </article>
      </div>
    </>
  );
}

function H({ children }: { children: ReactNode }) {
  return <h2 className="font-display text-base font-bold text-surface-950">{children}</h2>;
}

export function TerminosPage() {
  return (
    <LegalLayout title="Términos y condiciones">
      <p>
        Estos términos regulan la compra de equipos y kits de seguridad en la tienda en línea de{' '}
        <strong>{NOMBRE_COMERCIAL}</strong>, operada por {RAZON_SOCIAL} (en adelante, “Alarcom”).
        Al crear un pedido o pagar, usted acepta estas condiciones.
      </p>

      <section className="space-y-2">
        <H>1. Identidad del comercio</H>
        <p>
          Razón social: {RAZON_SOCIAL}. Nombre comercial: {NOMBRE_COMERCIAL}. Contacto:{' '}
          <a className="text-brand-700 underline" href={`mailto:${CONTACTO_LEGAL}`}>
            {CONTACTO_LEGAL}
          </a>
          . Sitio corporativo:{' '}
          <a className="text-brand-700 underline" href={SITIO_CORPORATIVO} target="_blank" rel="noreferrer">
            www.alarcom.mx
          </a>
          .
        </p>
      </section>

      <section className="space-y-2">
        <H>2. Productos y precios</H>
        <p>
          El catálogo incluye cámaras, grabadores, sensores, control de acceso, kits y refacciones
          disponibles para venta en línea. Los precios se muestran en pesos mexicanos (MXN). El
          subtotal no incluye IVA; el IVA (16%) se calcula en el carrito y en el checkout. Si
          solicita factura CFDI, el cobro incluye IVA. La disponibilidad y el precio vigentes son
          los que aparecen al confirmar el pedido.
        </p>
      </section>

      <section className="space-y-2">
        <H>3. Pedidos</H>
        <p>
          Puede comprar con cuenta de cliente Alarcom o como invitado. El pedido se registra en
          nuestro sistema con un folio web. Alarcom puede contactarle para confirmar datos de envío,
          existencias o facturación. Nos reservamos el derecho de rechazar o cancelar pedidos por
          error de precio, falta de stock o datos incompletos; en ese caso se reembolsa lo cobrado.
        </p>
      </section>

      <section className="space-y-2">
        <H>4. Pagos</H>
        <p>
          Los pagos con tarjeta de crédito o débito se procesan a través de{' '}
          <strong>Openpay</strong> (OPENPAY, S.A. de C.V.). Los datos de la tarjeta se tokenizan en
          el navegador y no se almacenan en servidores de Alarcom. El cargo puede requerir
          autenticación 3D Secure del banco emisor. También puede dejar el pedido con pago
          pendiente (transferencia o pago posterior); en ese caso el surtido inicia cuando se
          confirme el abono.
        </p>
        <p>
          Al pagar con Openpay usted también queda sujeto a sus{' '}
          <a className="text-brand-700 underline" href={OPENPAY_TERMINOS} target="_blank" rel="noreferrer">
            términos y condiciones
          </a>
          . Las transacciones viajan por HTTPS. Openpay cuenta con certificación PCI DSS.
        </p>
      </section>

      <section className="space-y-2">
        <H>5. Envíos</H>
        <p>
          Entregamos en territorio mexicano a la dirección indicada en el checkout. Los tiempos de
          surtido son estimados (por ejemplo, CDMX y zona metropolitana según operación de
          almacén) y se confirman al procesar el pedido. El riesgo de la mercancía se transmite al
          cliente al entregarse en el domicilio o al transportista, según el caso. Es
          responsabilidad del comprador proporcionar una dirección completa y un teléfono de
          contacto.
        </p>
      </section>

      <section className="space-y-2">
        <H>6. Cancelaciones, devoluciones y garantías</H>
        <p>
          Puede solicitar cancelación antes del envío escribiendo a {CONTACTO_LEGAL} con su folio.
          Productos con sello de garantía o empaque violado, usados o instalados no aplican a
          devolución salvo defecto de fabricación. Las garantías de fabricante se tramitan conforme
          a la póliza de cada marca y a la Ley Federal de Protección al Consumidor. Un cargo
          aprobado en Openpay no implica que Alarcom no pueda reembolsar si el pedido se cancela
          conforme a esta política.
        </p>
      </section>

      <section className="space-y-2">
        <H>7. Facturación</H>
        <p>
          Si marca “Requiero factura (CFDI)” debe proporcionar RFC, razón social y datos fiscales
          correctos. El CFDI se emite conforme a la normativa SAT vigente. Sin factura, el cobro
          corresponde al subtotal sin IVA, según se indica en el checkout.
        </p>
      </section>

      <section className="space-y-2">
        <H>8. Uso del sitio</H>
        <p>
          El contenido, marcas y fotografías de la tienda son de Alarcom o de sus proveedores. No
          está permitido usar el sitio para fraudes, pruebas de tarjetas ajenas o extracción
          masiva del catálogo.
        </p>
      </section>

      <section className="space-y-2">
        <H>9. Privacidad</H>
        <p>
          El tratamiento de datos personales se describe en el{' '}
          <Link className="text-brand-700 underline" to="/aviso-privacidad">
            Aviso de privacidad
          </Link>
          .
        </p>
      </section>

      <section className="space-y-2">
        <H>10. Ley aplicable</H>
        <p>
          Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Para controversias,
          las partes se someten a los tribunales competentes y a la PROFECO, en lo que resulte
          aplicable.
        </p>
      </section>

      <p className="text-xs text-surface-500">Última actualización: {FECHA_LEGAL}.</p>
    </LegalLayout>
  );
}

export function AvisoPrivacidadPage() {
  return (
    <LegalLayout title="Aviso de privacidad">
      <p>
        En cumplimiento de la Ley Federal de Protección de Datos Personales en Posesión de los
        Particulares, {RAZON_SOCIAL} (“Alarcom”, “nosotros”), con nombre comercial {NOMBRE_COMERCIAL},
        es responsable del tratamiento de sus datos personales.
      </p>

      <section className="space-y-2">
        <H>1. Datos que recabamos</H>
        <p>
          Identificación y contacto (nombre, correo, teléfono), dirección de envío, datos de
          facturación (RFC, razón social, uso CFDI, régimen) cuando usted los proporciona, y datos
          de la cuenta de cliente. Los datos de tarjeta los recibe directamente Openpay; Alarcom
          no almacena número de tarjeta, CVV ni fecha de vencimiento.
        </p>
      </section>

      <section className="space-y-2">
        <H>2. Finalidades</H>
        <p>
          Primarias: procesar pedidos, cobrar, facturar, enviar mercancía, dar seguimiento y
          soporte. Secundarias: mejorar el catálogo y comunicar promociones; puede oponerse
          escribiendo a {CONTACTO_LEGAL}.
        </p>
      </section>

      <section className="space-y-2">
        <H>3. Transferencias</H>
        <p>
          Compartimos con <strong>Openpay (OPENPAY, S.A. de C.V.)</strong> los datos necesarios para
          procesar el pago (nombre, correo, teléfono, dirección e importe). Openpay actúa como
          encargado / proveedor de servicios de pago con certificación PCI DSS. Consulte su{' '}
          <a className="text-brand-700 underline" href={OPENPAY_PRIVACIDAD} target="_blank" rel="noreferrer">
            aviso de privacidad
          </a>
          . También podemos compartir datos con paqueterías, el SAT (CFDI) y autoridades cuando la
          ley lo exija.
        </p>
      </section>

      <section className="space-y-2">
        <H>4. Seguridad</H>
        <p>
          El sitio de checkout opera sobre HTTPS (SSL). El pago con tarjeta usa tokenización
          Openpay.js y, cuando el banco lo requiere, 3D Secure. No solicitamos datos sensibles de
          tarjeta por correo ni WhatsApp.
        </p>
      </section>

      <section className="space-y-2">
        <H>5. Cookies</H>
        <p>
          Usamos cookies técnicas de sesión, carrito y autenticación. Puede deshabilitarlas en su
          navegador; algunas funciones de compra podrían dejar de operar.
        </p>
      </section>

      <section className="space-y-2">
        <H>6. Derechos ARCO y revocación</H>
        <p>
          Para acceder, rectificar, cancelar u oponerse al tratamiento, o para revocar su
          consentimiento, envíe un correo a{' '}
          <a className="text-brand-700 underline" href={`mailto:${CONTACTO_LEGAL}`}>
            {CONTACTO_LEGAL}
          </a>{' '}
          con su nombre, folio o correo de compra y la petición concreta. Responderemos en los
          plazos legales. También puede acudir al INAI si considera vulnerado su derecho a la
          protección de datos personales.
        </p>
      </section>

      <section className="space-y-2">
        <H>7. Cambios a este aviso</H>
        <p>
          Publicaremos actualizaciones en esta misma página y cambiaremos la fecha de última
          actualización. El uso posterior de la tienda implica conocimiento de la versión vigente.
        </p>
      </section>

      <p>
        Al continuar y al marcar la casilla en el checkout, usted entiende y acepta este aviso.
      </p>
      <p className="text-xs text-surface-500">Última actualización: {FECHA_LEGAL}.</p>
    </LegalLayout>
  );
}
